import {
  BookOpen,
  BowlFood,
  Briefcase,
  Calendar,
  CaretLeft,
  CaretRight,
  Check,
  GameController,
  Globe,
  Lightning,
  MapPin,
  Palette,
  SoccerBall,
  UsersThree,
  type Icon,
} from '@phosphor-icons/react';
import {
  EVENT_CATEGORY_COLORS,
  RESERVEES_AUX_PRIVES,
  LANGUES_PARLEES,
  type EventCategory,
  type LangueParlee,
} from '@sirocco/shared';
import { useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { CatalogueDeLangues } from '../../components/ui/CatalogueDeLangues';
import { openPicker } from '../../utils/picker';
import { t } from '../../i18n';
import { ficheDe } from '../../utils/langues';

/**
 * Le contenu de la fenetre de filtres.
 *
 * Il vit a part de l'ecran pour deux raisons : l'ecran des evenements
 * faisait deja trois cents lignes, et un panneau qui ne depend d'aucun
 * contexte se monte seul sur un banc d'essai — ce qui a servi.
 */

/** Ce que la fenetre regle : une periode, des categories, une nature. */
export interface Filtres {
  categories: Set<EventCategory>;
  from: string;
  to: string;
  /**
   * Ne garder que les sorties spontanees.
   *
   * Elles n'ont pas de categorie — c'est tout leur interet — donc elles
   * ne peuvent pas se filtrer comme le reste. Un interrupteur a part,
   * plutot qu'une cinquieme pastille de categorie qui ne dirait pas la
   * meme chose que les quatre autres.
   */
  spontanees: boolean;
  /**
   * Ne garder que les evenements en ligne. Avec les spontanees, l'un ou l'autre : ce sont deux natures, et une
   * sortie n'est jamais les deux a la fois.
   */
  enLigne: boolean;
  /**
   * Le rayon autour de moi, en kilometres. `null` = partout.
   *
   * Un spot ne couvre plus une ville mais une region : entre deux sorties
   * d'Ile-de-France il peut y avoir cent kilometres, et la liste seule ne
   * le dit pas. Le rayon est ce qui rend une region utilisable — sans
   * lui, « pres de chez moi » demande de lire chaque adresse.
   */
  rayon: number | null;
  /**
   * Les langues qu'on veut entendre. Ensemble vide = toutes.
   *
   * Une sortie passe si l'on y parle au moins l'une d'elles : une sortie
   * en francais et en anglais repond a qui ne cherche que l'anglais.
   */
  langues: Set<LangueParlee>;
}

export const AUCUN_FILTRE = (): Filtres => ({
  categories: new Set(),
  langues: new Set(),
  from: '',
  to: '',
  spontanees: false,
  enLigne: false,
  rayon: null,
});

/** Vrai des qu'un filtre retire quelque chose de la liste. */
export function filtreActif(f: Filtres): boolean {
  return (
    f.categories.size > 0 ||
    f.langues.size > 0 ||
    f.from !== '' ||
    f.to !== '' ||
    f.spontanees ||
    f.enLigne ||
    f.rayon !== null
  );
}

/**
 * Ordre des categories, celui du formulaire de creation — chacune avec
 * son signe. Un mot seul demandait d'etre lu ; un ballon, une palette,
 * une mallette et un bol se reconnaissent avant.
 */
/**
 * L'echelle du rail, en kilometres : de zero a cent.
 *
 * Le pas est le kilometre, parce que c'est l'unite dans laquelle la
 * question se pose. Cent au bout : Paris intra-muros tient dans dix
 * kilometres de large, et cent couvrent une region entiere.
 *
 * **Zero ne filtre rien.** Un rayon de zero kilometre ne contiendrait
 * aucune sortie, ce qui n'a aucun usage ; l'eteint, en revanche, doit
 * bien vivre quelque part. C'est le bout gauche du rail, la ou il n'est
 * pas peint du tout — l'oeil voit qu'aucune distance n'est demandee
 * avant meme d'avoir lu le chiffre.
 */
const MINIMUM = 0;
const MAXIMUM = 100;

const CATEGORIES: { value: EventCategory; icon: Icon }[] = [
  { value: 'sport', icon: SoccerBall },
  { value: 'culture', icon: Palette },
  { value: 'pro', icon: Briefcase },
  { value: 'food', icon: BowlFood },
  { value: 'asso', icon: UsersThree },
  { value: 'etudes', icon: BookOpen },
  { value: 'jeux', icon: GameController },
];

/**
 * Ou en est la localisation, telle que l'ecran la connait.
 *
 * `null` tant que personne n'a demande de rayon : la question ne se pose
 * pas, et poser une ligne a ce sujet serait du bruit.
 */
export type EtatLocalisation = 'attente' | 'trouvee' | 'introuvable' | null;

/**
 * Deplacer une borne sans jamais laisser la periode a l'envers.
 *
 * Le selecteur de la fin refuse deja les jours anterieurs au debut. Il
 * ne dit rien, en revanche, du debut qu'on repousse apres une fin deja
 * posee : c'est ainsi qu'on obtenait « du 23 au 22 septembre », une
 * periode qui ne contient rien et que la liste traduisait par un ecran
 * vide sans raison visible.
 *
 * L'autre borne suit plutot que de barrer la route. Poser un `max` sur
 * le debut aurait interdit la saisie, et enferme : une periode de
 * septembre qu'on veut porter en octobre se serait heurtee au mur du
 * 30 septembre, sans autre issue que de deviner qu'il faut changer la
 * fin d'abord. Ici le debut passe, la fin le rejoint, et l'on repart de
 * la journee ainsi formee — que le filtre sait deja traiter.
 */
function deplacerLeDebut(from: string) {
  return (current: Filtres): Filtres => ({
    ...current,
    from,
    to: current.to && from && current.to < from ? from : current.to,
  });
}

function deplacerLaFin(to: string) {
  return (current: Filtres): Filtres => ({
    ...current,
    to,
    from: current.from && to && to < current.from ? to : current.from,
  });
}

export function EventFilters({
  valeur,
  onChange,
  onAppliquer,
  onEffacer,
  localisation,
  campus = false,
  prive = false,
}: {
  valeur: Filtres;
  onChange: (maj: (current: Filtres) => Filtres) => void;
  onAppliquer: () => void;
  onEffacer: () => void;
  localisation: EtatLocalisation;
  /**
   * Sur un campus, pas de rail des kilometres : tout y est a quelques
   * minutes a pied, et cent kilometres autour de l'ecole ne trient rien.
   */
  campus?: boolean;
  /** Hors d'un serveur prive, pas de categorie d'ecole (`RESERVEES_AUX_PRIVES`). */
  prive?: boolean;
}) {
  const fromRef = useRef<HTMLInputElement>(null);
  const toRef = useRef<HTMLInputElement>(null);
  /* La fenetre montre les filtres, ou la liste des langues le temps d'en choisir. */
  const [vue, setVue] = useState<'filtres' | 'langues'>('filtres');
  const categories = prive
    ? CATEGORIES
    : CATEGORIES.filter(({ value }) => !RESERVEES_AUX_PRIVES.includes(value));

  function basculer(category: EventCategory) {
    onChange((current) => {
      const next = new Set(current.categories);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return { ...current, categories: next };
    });
  }

  if (vue === 'langues') {
    return (
      <div className="ev-filter__vue-langues">
        <div className="ev-filter__retour">
          <button
            type="button"
            className="sub-screen__back"
            aria-label={t.commun.retour}
            onClick={() => setVue('filtres')}
          >
            <CaretLeft className="sens-x" size={22} weight="bold" />
          </button>
          <p className="ev-filter__head">{t.sorties.filtres.langues}</p>
        </div>
        {/* Aucune cochee, c'est toutes : un filtre peut ne rien retenir. */}
        <CatalogueDeLangues
          className="ev-filter__catalogue"
          valeur={LANGUES_PARLEES.filter((code) => valeur.langues.has(code))}
          onChange={(langues: LangueParlee[]) =>
            onChange((current) => ({ ...current, langues: new Set(langues) }))
          }
          auMoinsUne={false}
        />
        <div className="ev-filter__actions">
          <Button size="md" onClick={() => setVue('filtres')}>
            {t.sorties.creation.languesTermine}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <p className="ev-filter__head">{t.sorties.filtres.titre}</p>

      {/*
        La periode d'abord : on cherche plus souvent « ce week-end »
        qu'« un evenement sportif ». Une seule borne suffit — a partir
        du, ou jusqu'au — et les deux au meme jour font une journee.
      */}
      <p className="ev-filter__label">{t.sorties.filtres.quand}</p>
      {/*
        Chaque borne est un `label`, et non un mot pose a cote du champ.

        Un champ de date ne fait que la hauteur de son texte — dix-huit
        pixels dans une pastille de quarante-six — et les mots « Du » et
        « au » comme les marges etaient des zones mortes. On appuyait sur
        la pastille, a cote de la cible, et rien ne s'ouvrait. Le `label`
        transmet l'appui a son champ, sans une ligne de JavaScript : toute
        la moitie de la pastille ouvre le calendrier qu'elle annonce.
      */}
      <div className="ev-filter__when">
        {/* Seize et non dix-huit : les deux pixels vont a la date, qui
            manquait de place une fois choisie. */}
        <Calendar size={16} />
        <label className="ev-filter__borne">
          <span className="ev-filter__mot">{t.sorties.filtres.du}</span>
          <input
            ref={fromRef}
            type="date"
            aria-label={t.sorties.filtres.aPartirDu}
            value={valeur.from}
            onClick={() => openPicker(fromRef.current)}
            onChange={(event) => onChange(deplacerLeDebut(event.target.value))}
          />
        </label>
        <label className="ev-filter__borne">
          <span className="ev-filter__mot">{t.sorties.filtres.au}</span>
          <input
            ref={toRef}
            type="date"
            aria-label={t.sorties.filtres.jusquAu}
            /*
              Le calendrier de la fin ne descend pas sous le debut : c'est
              la premiere barriere, celle qu'on voit — les jours anterieurs
              sont grises dans le selecteur.
            */
            min={valeur.from || undefined}
            value={valeur.to}
            onClick={() => openPicker(toRef.current)}
            onChange={(event) => onChange(deplacerLaFin(event.target.value))}
          />
        </label>
      </div>

      {!campus && (
        <>
          {/*
            Un rail plutot que des paliers.

            Cinq pastilles obligeaient a choisir entre 10 et 25 km quand on
            voulait 15. Le rail rend chaque kilometre atteignable, et le
            chiffre s'ecrit a cote du mot « Distance » : c'est lui qu'on lit
            en glissant, pas la position du point.
          */}
          <p className="ev-filter__label ev-filter__label--valeur">
            {t.sorties.filtres.distance}
            <span className="ev-filter__valeur">{valeur.rayon ?? MINIMUM} km</span>
          </p>
          <div className="ev-filter__rayon">
            <input
              type="range"
              className="ev-filter__curseur"
              min={MINIMUM}
              max={MAXIMUM}
              step={1}
              value={valeur.rayon ?? MINIMUM}
              aria-label={t.sorties.filtres.rayon}
              aria-valuetext={
                valeur.rayon === null
                  ? t.sorties.filtres.sansDistance
                  : t.sorties.filtres.kilometres(valeur.rayon)
              }
              /*
                L'avancee du rail, d'ou son bleu s'arrete.

                La fraction brute, sans la correction d'un demi-point qu'on
                applique d'ordinaire pour recaler un degrade sur le centre du
                curseur : elle laissait onze pixels de bleu tout a gauche,
                alors que le rail doit etre entierement eteint quand aucune
                distance n'est demandee. Ce que l'on montre ici est l'etat du
                filtre, pas la position exacte d'un point.
              */
              style={{
                ['--progres' as string]: `${
                  ((valeur.rayon ?? MINIMUM) / MAXIMUM) * 100
                }%`,
              }}
              onChange={(event) => {
                const km = Number(event.target.value);
                onChange((current) => ({ ...current, rayon: km === MINIMUM ? null : km }));
              }}
            />
            {/* Les deux bouts de l'echelle : on sait ce qu'on tient avant
                d'avoir glisse. */}
            <div className="ev-filter__jalons" aria-hidden="true">
              <span>{MINIMUM} km</span>
              <span>{MAXIMUM} km</span>
            </div>
          </div>

          {/*
            Une seule ligne, et seulement quand ca ne marche pas.

            Il y en avait trois : la recherche en cours, la position trouvee,
            la position introuvable. Les deux premieres commentaient un
            fonctionnement normal — le rail montre deja qu'il filtre — et
            elles paraissaient a chaque ouverture pour ne rien apprendre.

            La troisieme reste. Sans elle, choisir un rayon sans autorisation
            ne fait rien du tout et rien ne dit pourquoi : le filtre aurait
            l'air casse. Neutre a dessein, car le meme build tourne dans un
            navigateur ou « les reglages de ton telephone » ne voudrait rien
            dire.
          */}
          {localisation === 'introuvable' && (
            <p className="ev-filter__note">
              <MapPin size={14} weight="fill" />
              {t.sorties.filtres.sansPosition}
            </p>
          )}
        </>
      )}

      <p className="ev-filter__label">{t.sorties.filtres.nature}</p>
      {/* Chaque nature sur sa ligne, a sa largeur : cote a cote, « Sorties spontanees » se recoupait. */}
      <div className="ev-filter ev-filter--large">
        <button
          className={`ev-filter__pill${valeur.spontanees ? ' ev-filter__pill--on' : ''}`}
          aria-pressed={valeur.spontanees}
          style={{ ['--cat' as string]: 'var(--s-primary)' }}
          onClick={() => onChange((current) => ({ ...current, spontanees: !current.spontanees }))}
        >
          <Lightning size={17} weight="fill" />
          <span className="ev-filter__nom">{t.sorties.filtres.spontanees}</span>
          {valeur.spontanees && (
            <span className="ev-filter__coche" aria-hidden="true">
              <Check size={11} weight="bold" />
            </span>
          )}
        </button>
        <button
          className={`ev-filter__pill${valeur.enLigne ? ' ev-filter__pill--on' : ''}`}
          aria-pressed={valeur.enLigne}
          style={{ ['--cat' as string]: 'var(--s-primary)' }}
          onClick={() => onChange((current) => ({ ...current, enLigne: !current.enLigne }))}
        >
          <Globe size={17} />
          <span className="ev-filter__nom">{t.sorties.filtres.enLigne}</span>
          {valeur.enLigne && (
            <span className="ev-filter__coche" aria-hidden="true">
              <Check size={11} weight="bold" />
            </span>
          )}
        </button>
      </div>

      <p className="ev-filter__label">{t.sorties.filtres.categories}</p>
      <div className="ev-filter">
        {categories.map(({ value, icon: Signe }) => {
          const on = valeur.categories.has(value);
          return (
            <button
              key={value}
              className={`ev-filter__pill${on ? ' ev-filter__pill--on' : ''}`}
              aria-pressed={on}
              style={{ ['--cat' as string]: EVENT_CATEGORY_COLORS[value] }}
              onClick={() => basculer(value)}
            >
              <Signe size={17} />
              <span className="ev-filter__nom">{t.sorties.categories[value]}</span>
              {on && (
                <span className="ev-filter__coche" aria-hidden="true">
                  <Check size={11} weight="bold" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/*
        Les langues en dernier : la plupart des membres n'y toucheront pas,
        puisque les sorties dans les leurs remontent deja en tete. Une ligne,
        et non des pastilles : quarante langues ne tiennent pas dans une
        fenetre. La toucher ouvre la liste, avec sa recherche.
      */}
      <p className="ev-filter__label">{t.sorties.filtres.langues}</p>
      <button type="button" className="ev-filter__langues" onClick={() => setVue('langues')}>
        <span className="ev-filter__langues-choix">
          {valeur.langues.size === 0
            ? t.sorties.filtres.toutesLangues
            : LANGUES_PARLEES.filter((code) => valeur.langues.has(code)).map((code) => {
                const { nom, drapeau } = ficheDe(code);
                return (
                  <span key={code} className="langues-champ__langue">
                    <span className="drapeau" aria-hidden="true">
                      {drapeau}
                    </span>
                    <span lang={code}>{nom}</span>
                  </span>
                );
              })}
        </span>
        <CaretRight size={16} weight="bold" className="sens-x langues-champ__fleche" />
      </button>

      <div className="ev-filter__actions">
        <Button size="md" onClick={onAppliquer}>
          {t.sorties.filtres.appliquer}
        </Button>
        <Button size="md" variant="danger" onClick={onEffacer}>
          {t.sorties.filtres.effacer}
        </Button>
      </div>
    </>
  );
}
